import { createContext, useContext } from "react"

import { AIModel } from "@llumiverse/core"
import { VertesiaClient } from "@vertesia/client"

import { SharedState, useWatchSharedState } from "@hooks/use-shared-state"

interface AvailableModels {
    models: AIModel[]
    error?: Error
    loading: boolean
}

export class AvailableModel {
    result = new SharedState<AvailableModels>({
        models: [],
        error: undefined,
        loading: false,
    })

    constructor(public client: VertesiaClient) {}

    get models() {
        return this.result.value.models
    }

    get error() {
        return this.result.value.error
    }

    get isLoading() {
        return this.result.value.loading
    }

    reset() {
        this.result.value = {
            models: [],
            error: undefined,
            loading: false,
        }
    }

    async _search() {
        if (this.isLoading) {
            return Promise.resolve(false)
        }

        this.result.value = {
            models: [],
            error: undefined,
            loading: false,
        }

        try {
            const environments = await this.client.environments.list()
            this.result.value = {
                models: environments.flatMap((env) =>
                    (env.enabled_models || []).map((model) => ({
                        ...model,
                        environment: env.id,
                    }))
                ),
                error: undefined,
                loading: false,
            }

            return true
        } catch (err) {
            this.result.value = {
                models: [],
                error: err instanceof Error ? err : new Error("Unknown error occurred"),
                loading: false,
            }
            throw err
        }
    }

    search() {
        if (this.isLoading) {
            return Promise.resolve(false)
        }
        return this._search()
    }
}

export const AvailableModelContext = createContext<AvailableModel>(new AvailableModel(new VertesiaClient()))

export function useSearch() {
    return useContext(AvailableModelContext)!
}

export function useWatchSearchResult() {
    const search = useSearch()
    const result = useWatchSharedState(search.result)
    return { ...result, search }
}
